import React, { useState } from 'react';
import styled from 'styled-components';
import Modal from 'components/UI/Modal';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import { Border, Main, Text } from 'styles/themeColors';
import { FontWeight } from 'lib/styles';
import { TEXT_LABELS } from 'constants/order';
import Warning from '@mui/icons-material/Warning';

const WarningBox = styled(FlexboxContainer)`
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 16px;
`;

const WarningText = styled.span`
  font-size: 14px;
  color: ${Text.primary};
  white-space: pre-line;
`;

const LabelText = styled.span`
  display: block;
  font-weight: ${FontWeight.medium};
  text-align: left;
  margin-bottom: 5px;
  font-size: 15px;
  color: ${Text.primary};
`;

const TextArea = styled.textarea`
  border: 1px solid ${Border.main};
  border-radius: 6px;
  padding: 7px;
  color: ${Text.primary};
  resize: vertical;
`;

export interface Props {
  show: boolean;
  onClose: () => void;
  /** Called with the entered reason when the user confirms */
  onConfirm: (reason: string) => void;
  /** Loading state controlled by the caller's API request */
  isLoading?: boolean;
  disabled?: boolean;
  title?: string;
  warningText?: string;
  reasonLabel?: string;
  confirmButtonText?: string;
  onSave?: () => void;
  ghostButtonText?: string;
}

const CancellationReasonModal = ({
  show,
  onClose,
  onConfirm,
  isLoading,
  disabled = false,
  title,
  warningText,
  reasonLabel,
  confirmButtonText,
  ghostButtonText,
  onSave
}: Props) => {
  const [reason, setReason] = useState('');

  const handleClose = () => {
    setReason('');
    onClose();
  };

  const handleConfirm = () => {
    // Delegates the API call to the caller; caller is responsible for close/toast on resolve
    onConfirm(reason);
  };

  return (
    <Modal
      showButtons
      showModal={show}
      title={title ?? TEXT_LABELS.CANCEL_SUBSCRIPTION_TITLE}
      onClose={handleClose}
      onClick={handleConfirm}
      disabled={disabled || !reason.trim() || !!isLoading}
      ghostButtonText={ghostButtonText ?? TEXT_LABELS.SUBSCRIPTION_MODAL_CLOSE}
      primaryButtonText={confirmButtonText ?? TEXT_LABELS.CANCEL_SUBSCRIPTION_CONFIRM}
      width="560px"
      primaryButtonColor={Main.error}
      color={Text.primary}
      borderColor={Text.primary}
    >
      <FlexboxContainer flexDirection="column">
        <WarningBox flexDirection="row" alignItems="flex-start" gap="8px">
          <Warning style={{ fontSize: 20, marginTop: 35, flexShrink: 0 }} />
          <WarningText>{warningText ?? TEXT_LABELS.CANCEL_SUBSCRIPTION_WARNING}</WarningText>
        </WarningBox>
        <LabelText>{reasonLabel ?? TEXT_LABELS.CANCEL_SUBSCRIPTION_REASON}</LabelText>
        <TextArea
          maxLength={500}
          rows={5}
          value={reason}
          placeholder="Reason"
          onChange={(e) => setReason(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.stopPropagation();
            }
          }}
        />
      </FlexboxContainer>
    </Modal>
  );
};

export default CancellationReasonModal;
